import React, { useState, useEffect } from 'react';
import { Button, Form, Table, Badge, Alert, Modal } from 'react-bootstrap';
import { workoutAPI } from '../services/workoutAPI';

function SetLogger({ exercise, onUpdate, mode = 'logging' }) {
  const [sets, setSets] = useState(exercise.sets || []);
  const [exerciseHistory, setExerciseHistory] = useState([]); // Changed to array
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);

  const isWeightReps = exercise.exercise_type === 'weight_reps';
  const isWeightTime = exercise.exercise_type === 'weight_time';
  const isTemplateMode = mode === 'template';

  useEffect(() => {
    // Only fetch history in logging mode
    if (!isTemplateMode) {
      fetchExerciseHistory();
    }
  }, [exercise.exercise_id, isTemplateMode]);

  useEffect(() => {
    // Update parent component when sets change
    onUpdate({ sets });
  }, [sets]);

  const fetchExerciseHistory = async () => {
    setLoadingHistory(true);
    try {
      const history = await workoutAPI.fetchExerciseHistory(exercise.exercise_id);
      if (history && history.length > 0) {
        setExerciseHistory(history.slice(0, 3)); // Last 3 sessions
      }
    } catch (err) {
      console.error('Error fetching exercise history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleAddSet = () => {
    const newSet = {
      set_number: sets.length + 1,
      weight: '',
      weight_unit: 'lbs',
      reps: isWeightReps ? '' : undefined,
      reps_prescription: isWeightReps && isTemplateMode ? '' : undefined,
      duration_seconds: isWeightTime ? '' : undefined,
      notes: ''
    };
    setSets([...sets, newSet]);
  };

  const handleRemoveSet = (index) => {
    const confirmed = window.confirm('Delete this set?');
    if (!confirmed) return;

    const newSets = sets.filter((_, i) => i !== index);
    // Renumber sets
    const renumberedSets = newSets.map((set, i) => ({
      ...set,
      set_number: i + 1
    }));
    setSets(renumberedSets);
  };

  const handleSetChange = (index, field, value) => {
    const newSets = sets.map((set, i) => {
      if (i === index) {
        return { ...set, [field]: value };
      }
      return set;
    });
    setSets(newSets);
  };

  const handleCopyFromLast = () => {
    if (!exerciseHistory || exerciseHistory.length === 0 || !exerciseHistory[0].sets) return;

    const mostRecent = exerciseHistory[0];
    const copiedSets = mostRecent.sets.map((set, index) => ({
      set_number: index + 1,
      weight: set.weight || '',
      weight_unit: set.weight_unit || 'lbs',
      reps: isWeightReps ? (set.reps || '') : undefined,
      duration_seconds: isWeightTime ? (set.duration_seconds || '') : undefined,
      notes: ''
    }));

    setSets(copiedSets);
  };

  const formatSessionPerformance = (session) => {
    if (!session || !session.sets || session.sets.length === 0) {
      return 'No previous data';
    }

    const setCount = session.sets.length;
    const firstSet = session.sets[0];

    // Extract weights and check if they're all the same (handle null/undefined/empty as 0)
    const weights = session.sets.map(s => {
      const weight = parseFloat(s.weight);
      return isNaN(weight) ? 0 : weight;
    });
    const allSameWeight = weights.every(w => w === weights[0]);

    // Format weight display - show single weight if all same, otherwise show all
    const weightDisplay = allSameWeight
      ? `${weights[0]}`
      : weights.join('/');

    if (isWeightReps) {
      const reps = session.sets.map(s => s.reps).join('/');
      return `${setCount} set${setCount === 1 ? '' : 's'}: ${reps} reps @ ${weightDisplay} ${firstSet.weight_unit}`;
    } else if (isWeightTime) {
      const durations = session.sets.map(s => `${s.duration_seconds}s`).join('/');
      return `${setCount} set${setCount === 1 ? '' : 's'}: ${durations} @ ${weightDisplay} ${firstSet.weight_unit}`;
    }

    return 'Unknown format';
  };

  const formatTimeAgo = (date) => {
    if (!date) return '';

    const now = new Date();
    const past = new Date(date);
    const diffDays = Math.floor((now - past) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  // Get prescription display for a set (for logging mode)
  const getPrescriptionDisplay = (set) => {
    // First check reps_prescription, then fall back to reps for backward compatibility
    if (set.reps_prescription) {
      return set.reps_prescription;
    }
    // For old templates that only have numeric reps, show that
    if (set.reps !== undefined && set.reps !== '' && set.reps !== null) {
      return String(set.reps);
    }
    return null;
  };

  return (
    <div className="set-logger">
      {/* Last Performance Icon Button - only show in logging mode */}
      {!isTemplateMode && !loadingHistory && exerciseHistory && exerciseHistory.length > 0 && (
        <div className="mb-2">
          <Button
            variant="link"
            size="sm"
            onClick={() => setShowPerformanceModal(true)}
            className="p-0 text-info"
            title="View last performance"
          >
            <i className="fas fa-info-circle me-1"></i>
            Last performance
          </Button>
        </div>
      )}

      {/* Sets Table */}
      {sets.length > 0 ? (
        <Table striped bordered size="sm" className="mb-3">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>Set</th>
              <th>Weight</th>
              <th style={{ width: '60px' }}>Unit</th>
              {isWeightReps && <th>{isTemplateMode ? 'Reps (Prescription)' : 'Reps'}</th>}
              {isWeightTime && <th>Duration (sec)</th>}
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {sets.map((set, index) => (
              <tr key={index}>
                <td className="text-center">
                  <Badge bg="primary">{set.set_number}</Badge>
                </td>
                <td>
                  <Form.Control
                    type="number"
                    size="sm"
                    step="0.5"
                    placeholder="0"
                    value={set.weight}
                    onChange={(e) => handleSetChange(index, 'weight', e.target.value)}
                  />
                </td>
                <td>
                  <Form.Select
                    size="sm"
                    value={set.weight_unit}
                    onChange={(e) => handleSetChange(index, 'weight_unit', e.target.value)}
                  >
                    <option value="lbs">lbs</option>
                    <option value="kg">kg</option>
                  </Form.Select>
                </td>
                {isWeightReps && (
                  <td>
                    {isTemplateMode ? (
                      /* Template mode: Text input for prescription (e.g., "8-10", "AMRAP") */
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="e.g., 8-10"
                        value={set.reps_prescription || ''}
                        onChange={(e) => handleSetChange(index, 'reps_prescription', e.target.value)}
                      />
                    ) : (
                      /* Logging mode: Show prescription label above numeric input */
                      <div className="reps-input-container">
                        {getPrescriptionDisplay(set) && (
                          <div className="reps-prescription">
                            {getPrescriptionDisplay(set)}
                          </div>
                        )}
                        <Form.Control
                          type="number"
                          size="sm"
                          placeholder="0"
                          value={set.reps || ''}
                          onChange={(e) => handleSetChange(index, 'reps', e.target.value)}
                        />
                      </div>
                    )}
                  </td>
                )}
                {isWeightTime && (
                  <td>
                    <Form.Control
                      type="number"
                      size="sm"
                      placeholder="0"
                      value={set.duration_seconds || ''}
                      onChange={(e) => handleSetChange(index, 'duration_seconds', e.target.value)}
                    />
                  </td>
                )}
                <td className="text-center">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleRemoveSet(index)}
                  >
                    <i className="fas fa-trash"></i>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div className="text-center py-3 mb-3 border rounded bg-light">
          <p className="text-muted mb-0">No sets added yet</p>
        </div>
      )}

      {/* Add Set Button */}
      <Button
        variant="outline-primary"
        size="sm"
        onClick={handleAddSet}
        className="w-100"
      >
        <i className="fas fa-plus me-1"></i> Add Set
      </Button>

      {/* Performance Modal - only in logging mode */}
      {!isTemplateMode && (
        <Modal show={showPerformanceModal} onHide={() => setShowPerformanceModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Last {exerciseHistory.length} Performance{exerciseHistory.length > 1 ? 's' : ''}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {exerciseHistory && exerciseHistory.length > 0 ? (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {exerciseHistory.map((session, index) => (
                  <div
                    key={session.id || index}
                    className="mb-3 p-3 border rounded"
                    style={{ backgroundColor: index === 0 ? '#f8f9fa' : '#ffffff' }}
                  >
                    {/* Session Badge and Date */}
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <Badge bg={index === 0 ? 'primary' : 'secondary'}>
                        {index === 0 ? 'Most Recent' : `${index + 1} session${index === 1 ? '' : 's'} ago`}
                      </Badge>
                      <small className="text-muted">
                        {new Date(session.workout_date).toLocaleDateString()} ({formatTimeAgo(session.workout_date)})
                      </small>
                    </div>

                    {/* Performance Data */}
                    <div>
                      <strong>{formatSessionPerformance(session)}</strong>
                    </div>

                    {/* Session Notes */}
                    {session.notes && (
                      <div className="mt-2 pt-2 border-top">
                        <small className="text-muted d-block">Notes:</small>
                        <div className="text-muted" style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                          {session.notes}
                        </div>
                      </div>
                    )}

                    {/* Workout Name */}
                    {session.workout_name && (
                      <div className="mt-1">
                        <small className="text-muted">
                          <i className="fas fa-dumbbell me-1"></i>
                          {session.workout_name}
                        </small>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted mb-0">No previous performance data</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowPerformanceModal(false)}>
              Close
            </Button>
            {exerciseHistory && exerciseHistory.length > 0 && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  handleCopyFromLast();
                  setShowPerformanceModal(false);
                }}
              >
                <i className="fas fa-copy me-1"></i> Copy Most Recent
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default SetLogger;
